'use strict';

/**
 * Pict-Provider-BoardAPI
 * ----------------------
 * Backs the bespoke Kanban board: a stream of Work Item cards bucketed into the
 * tenant's lanes by Status and sorted by SortOrder. Moving a card PUTs the
 * entity's Status/SortOrder, which trips the server-side status-change audit
 * (WorkflowStatusEvent).
 *
 * This board once carried Features too, as one unified "trackable card" stream.
 * They moved to a board of their own (Pict-Provider-FeatureBoardAPI): work items
 * move at agent speed and features move at product speed, and one surface implied
 * they should move together. Features are still FETCHED here, because a Work Item
 * card names what it is for and "F-167" needs that Feature's number; they are just
 * no longer cards.
 *
 * AppData.Board = { Lanes: [{ Key, Label, Cards: [card], Count }], Loading, Error }
 *   card = { CardKind, ID, Title, Status, SortOrder, Type, Priority, KindClass }
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libDescriptors = require('../EntityDescriptors.js');
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'BoardAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

const _SORT_STEP = 100; // sparse SortOrder so a drop rarely renumbers a lane

class PictProviderBoardAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
		// The lane definitions the board buckets into. Null until list() fetches the
		// tenant's lanes from the store; _laneDefs() falls back to the default set so
		// _bucket works in a fresh / unit-test context with no server fetch.
		this._laneDefinitions = null;
	}

	// The lane set the board renders: the fetched store lanes, or the default seed
	// when nothing has been fetched (fresh install, or the pure-model unit tests).
	_laneDefs()
	{
		if (this._laneDefinitions && this._laneDefinitions.length) { return this._laneDefinitions; }
		return libDescriptors.DefaultBoardLanes;
	}

	// Fetch this tenant's lanes from the lane store. Resolves to the lane definitions
	// (and caches them for _bucket). Falls back to the default set on any error so the
	// board always has lanes to draw into.
	loadLanes()
	{
		let tmpLaneAPI = this.fable && this.fable.providers && this.fable.providers.LaneAPI;
		if (!tmpLaneAPI)
		{
			this._laneDefinitions = libDescriptors.DefaultBoardLanes.slice();
			return Promise.resolve(this._laneDefinitions);
		}
		return tmpLaneAPI.listLanes()
			.then((pLanes) =>
			{
				this._laneDefinitions = (Array.isArray(pLanes) && pLanes.length) ? pLanes : libDescriptors.DefaultBoardLanes.slice();
				return this._laneDefinitions;
			})
			.catch(() =>
			{
				this._laneDefinitions = libDescriptors.DefaultBoardLanes.slice();
				return this._laneDefinitions;
			});
	}

	store()
	{
		if (!this.fable.AppData.Board)
		{
			this.fable.AppData.Board = { Lanes: [], Loading: false, Error: null };
		}
		return this.fable.AppData.Board;
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') tmpPrefix += '/';
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_get(pPluralRoute)
	{
		// Through the shared RestClient so a 401 here drives session recovery.
		return libFrameworkClient.getJSON(this.fable, this._url(pPluralRoute + '/0/500'))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || []);
	}

	// Whether the signed-in user can sign off on review (the content.approve gate the Review
	// endpoint's engine enforces). Gates the on-card Passed/Send-back buttons so they only appear
	// to reviewers; the server enforces it regardless.
	_canApprove()
	{
		let tmpUser = (this.fable.AppData.Auth && this.fable.AppData.Auth.User) || {};
		if (tmpUser.IsPlatformAdmin === 1 || tmpUser.IsPlatformAdmin === true) { return true; }
		return Array.isArray(tmpUser.Entitlements) && tmpUser.Entitlements.indexOf('content.approve') >= 0;
	}

	_toCard(pKind, pRow)
	{
		return (
		{
			CardKind: pKind,
			ID: (pKind === 'Feature') ? pRow.IDFeature : pRow.IDWorkItem,
			// The QC review affordance: present only for a Work Item sitting in the Review lane, and
			// only for a user who can sign off. Empty array -> the template-set renders nothing.
			ReviewSlot: (pKind !== 'Feature' && pRow.Status === 'Review' && this._canApprove())
				? [{ ID: pRow.IDWorkItem, ChangesRequested: (pRow.WorkflowStatus === 'ChangesRequested') ? 1 : 0 }] : [],
			// The per-tenant WorkItemNumber a person sees, and what the bulk selector keys on
			// (0 for Features, which v1 bulk does not act on).
			Number: (pKind === 'Feature') ? 0 : (Number(pRow.WorkItemNumber) || 0),
			Title: ((pKind === 'Feature') ? pRow.Name : pRow.Title) || ('#' + ((pKind === 'Feature') ? pRow.IDFeature : pRow.IDWorkItem)),
			Status: pRow.Status || 'Backlog',
			// Carried so the focus-view engine can filter on the workflow sub-state (e.g. the
			// ChangesRequested marker a did-not-pass-QC kickback sets). Features have none.
			WorkflowStatus: (pKind === 'Feature') ? '' : (pRow.WorkflowStatus || ''),
			SortOrder: Number(pRow.SortOrder) || 0,
			Type: pRow.Type || (pKind === 'Feature' ? 'Feature' : ''),
			Priority: pRow.Priority || '',
			// Carried so the focus-view engine can filter cards the same way it filters the
			// Work Items list: by assignee (human / AI), recent activity, and a text search
			// over Title + Description. Features have no TargetType, so a target facet skips them.
			IDAssignedUser: Number(pRow.IDAssignedUser) || 0,
			Description: pRow.Description || '',
			TargetType: (pKind === 'Feature') ? '' : (pRow.TargetType || ''),
			// The target's own id, kept so _resolveMarks can turn it into that entity's designator
			// (F-167) once every card is in hand. Nothing renders these directly.
			IDTargetFeature: (pKind === 'Feature') ? 0 : (Number(pRow.IDTargetFeature) || 0),
			IDTargetVision: (pKind === 'Feature') ? 0 : (Number(pRow.IDTargetVision) || 0),
			IDTargetModule: (pKind === 'Feature') ? 0 : (Number(pRow.IDTargetModule) || 0),
			// The per-tenant number of THIS card's own entity, which is what its designator is built
			// from. Features carry FeatureNumber; the WorkItem case already lives on Number above.
			FeatureNumber: (pKind === 'Feature') ? (Number(pRow.FeatureNumber) || 0) : 0,
			UpdateDate: pRow.UpdateDate || '',
			CreateDate: pRow.CreateDate || '',
			KindClass: (pKind === 'Feature') ? 'ps-card-feature' : 'ps-card-workitem'
		});
	}

	/**
	 * Make sure the team roster is in hand before marks are resolved, because a card names who holds
	 * each item and the roster is the only place that name lives. Loaded once and left on AppData.Team,
	 * which every other surface already reads, so a board that has it does not fetch it again.
	 *
	 * Never rejects: a roster this board could not fetch costs one cell its value, and must not cost
	 * the whole board its cards.
	 */
	_ensureRoster()
	{
		// AppData.Team.Roster, not Members: the board already loads the roster for its assignee facet
		// (PictView-Board loadRoster / applyFilter), so the card reads the list that is already there
		// rather than fetching a second, parallel copy of the same people.
		let tmpTeam = this.fable.AppData.Team;
		if (tmpTeam && Array.isArray(tmpTeam.Roster) && tmpTeam.Roster.length)
		{
			return Promise.resolve(tmpTeam.Roster);
		}
		let tmpTeamAPI = this.fable.providers.TeamAPI;
		if (!tmpTeamAPI || typeof tmpTeamAPI.roster !== 'function') { return Promise.resolve([]); }
		return tmpTeamAPI.roster().catch(() => []);
	}

	// The assignee as a person reads it. Resolved from the team roster when it is already loaded and
	// degraded to a dash when it is not, so a board that never asked for the roster still renders a
	// complete card rather than an empty cell.
	_assignee(pIDUser)
	{
		// Nobody is holding it. A dash says that in the width available; "Unassigned" only ever renders
		// as "Unas...", which is worse than the mark it replaced.
		if (!pIDUser) { return ''; }
		let tmpTeam = this.fable.AppData.Team || {};
		let tmpPeople = (Array.isArray(tmpTeam.Roster) && tmpTeam.Roster.length) ? tmpTeam.Roster : (tmpTeam.Members || []);
		let tmpMember = tmpPeople.filter((pMember) => Number(pMember.IDUser) === Number(pIDUser))[0];
		if (!tmpMember) { return ''; }
		let tmpName = tmpMember.DisplayName || tmpMember.Email || '';
		// An agent is a first-class assignee here, and saying so on the card is the difference between
		// reading a lane and having to open things to find out who is holding what.
		return (tmpMember.IsAgent === 1 || tmpMember.IsAgent === true) ? (tmpName + ' (AI)') : tmpName;
	}

	/**
	 * Fill in each card's Mark and Cells. This runs after every card exists, because a Work Item's
	 * target is another card: resolving F-167 means finding that Feature's own number, which is only
	 * possible once the Features are built too.
	 *
	 * A target that cannot be resolved degrades to its type ("Vision") rather than rendering blank, so
	 * a cell never silently disappears and shift the ones after it.
	 */
	_resolveMarks(pCards, pFeatures)
	{
		let tmpMarkup = this.fable.providers.Markup;
		if (!tmpMarkup) { return pCards; }

		// Features are no longer cards on this board (they have their own), so their numbers come
		// from the fetched rows. A Work Item still says what it is FOR, and "F-167" is the only
		// form of that answer a reader can act on.
		let tmpFeatureByID = {};
		(Array.isArray(pFeatures) ? pFeatures : []).forEach((pFeature) =>
		{
			tmpFeatureByID[pFeature.IDFeature] = { FeatureNumber: Number(pFeature.FeatureNumber) || 0 };
		});

		pCards.forEach((pCard) =>
		{
			let tmpEntity = (pCard.CardKind === 'Feature') ? 'Feature' : 'WorkItem';
			let tmpNumber = (pCard.CardKind === 'Feature') ? pCard.FeatureNumber : pCard.Number;

			let tmpTarget = '';
			if (pCard.IDTargetFeature && tmpFeatureByID[pCard.IDTargetFeature])
			{
				tmpTarget = tmpMarkup.designator('Feature', tmpFeatureByID[pCard.IDTargetFeature].FeatureNumber);
			}
			// Vision and Module targets are not on the board, so their numbers are not in hand. Name the
			// kind rather than guessing at a designator that would be wrong.
			if (!tmpTarget && pCard.TargetType) { tmpTarget = pCard.TargetType; }

			pCard.Designator = tmpMarkup.designator(tmpEntity, tmpNumber);
			// The entity's own number, normalized across kinds so a search for "19" can match a Feature
			// as readily as a Work Item (Number is 0 on a Feature card, which the bulk selector relies on).
			pCard.MarkNumber = Number(tmpNumber) || 0;
			pCard.Mark = tmpMarkup.mark(tmpEntity, { Title: pCard.Title }, {});
			pCard.Mark.Designator = pCard.Designator;
			pCard.Mark.KindClass = pCard.KindClass;
			pCard.Cells =
			[
				{ K: 'Type', V: pCard.Type },
				{ K: 'Pri', V: pCard.Priority },
				{ K: 'By', V: this._assignee(pCard.IDAssignedUser) },
				{ K: 'For', V: tmpTarget }
			].map((pCell) => ({ K: pCell.K, V: (pCell.V === null || typeof pCell.V === 'undefined' || String(pCell.V).trim() === '') ? '—' : String(pCell.V) }));
		});
		return pCards;
	}

	_bucket(pCards)
	{
		let tmpStore = this.store();
		let tmpDefs = this._laneDefs();
		let tmpLanes = tmpDefs.map((pDef) => ({ Key: pDef.Key, Label: pDef.Label || pDef.Key, Color: pDef.Color || '', IsTerminal: !!pDef.IsTerminal, Cards: [], Count: 0 }));
		let tmpByKey = {};
		for (let i = 0; i < tmpLanes.length; i++) tmpByKey[tmpLanes[i].Key] = tmpLanes[i];
		// A Status with no matching lane (older data, or a lane was renamed/removed)
		// falls back to the first lane so no card ever disappears from the board.
		let tmpFallback = tmpLanes[0] || null;

		pCards.sort((pA, pB) => pA.SortOrder - pB.SortOrder);
		for (let i = 0; i < pCards.length; i++)
		{
			let tmpLane = tmpByKey[pCards[i].Status] || tmpFallback;
			if (tmpLane) tmpLane.Cards.push(pCards[i]);
		}
		for (let i = 0; i < tmpLanes.length; i++) tmpLanes[i].Count = tmpLanes[i].Cards.length;
		tmpStore.Lanes = tmpLanes;
	}

	list()
	{
		let tmpStore = this.store();
		tmpStore.Loading = true;
		return Promise.all([this._get('Features'), this._get('WorkItems'), this.loadLanes(), this._ensureRoster()])
			.then((pResults) =>
			{
				let tmpCards = [];
				let tmpFeatures = pResults[0] || [];
				let tmpWorkItems = pResults[1] || [];
				// Archived work items are soft-removed from the board (recoverable, still readable
				// elsewhere), so they never bucket into a lane.
				for (let i = 0; i < tmpWorkItems.length; i++)
				{
					if (Number(tmpWorkItems[i].Archived) === 1 || tmpWorkItems[i].Archived === true) continue;
					tmpCards.push(this._toCard('WorkItem', tmpWorkItems[i]));
				}
				// Marks are resolved once, here, because a card's target is another card: turning a
				// Work Item's target into F-167 needs its Feature's own number, which is why the
				// Features are still fetched even though they no longer become cards.
				this._resolveMarks(tmpCards, tmpFeatures);
				// Keep the unfiltered card set so a focus-view filter can re-bucket the board
				// without re-fetching (see rebucket()); the board view owns the predicate.
				tmpStore.AllCards = tmpCards;
				this._bucket(tmpCards);
				tmpStore.Loading = false;
				tmpStore.Error = null;
				return tmpStore.Lanes;
			})
			.catch((pErr) =>
			{
				tmpStore.Loading = false;
				tmpStore.Error = pErr.message;
				return [];
			});
	}

	// The unfiltered card set from the last list() (Features + non-archived WorkItems).
	allCards()
	{
		return (this.store().AllCards || []).slice();
	}

	// Re-bucket the lanes from a (usually filtered) subset of cards, without a re-fetch.
	// Passing nothing restores the full board. Returns the rebuilt lanes.
	rebucket(pCards)
	{
		this._bucket(Array.isArray(pCards) ? pCards.slice() : this.allCards());
		return this.store().Lanes;
	}

	/**
	 * Next sparse SortOrder for appending a card to the end of a lane.
	 */
	nextSortOrder(pLaneKey)
	{
		let tmpStore = this.store();
		let tmpMax = 0;
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			if (tmpStore.Lanes[i].Key !== pLaneKey) continue;
			let tmpCards = tmpStore.Lanes[i].Cards;
			for (let j = 0; j < tmpCards.length; j++)
			{
				if (tmpCards[j].SortOrder > tmpMax) tmpMax = tmpCards[j].SortOrder;
			}
		}
		return tmpMax + _SORT_STEP;
	}

	/**
	 * Locate a card in the model. Returns { Card, Lane, Index } or null.
	 */
	_findCard(pKind, pID)
	{
		let tmpStore = this.store();
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			let tmpCards = tmpStore.Lanes[i].Cards;
			for (let j = 0; j < tmpCards.length; j++)
			{
				if (tmpCards[j].CardKind === pKind && tmpCards[j].ID === pID)
				{
					return { Card: tmpCards[j], Lane: tmpStore.Lanes[i], Index: j };
				}
			}
		}
		return null;
	}

	/**
	 * Pick the SortOrder for a card dropped at pIndex within pDest (the target lane's
	 * cards WITHOUT the moved card). Between two neighbors we take the integer
	 * midpoint; at the top or bottom we step one sparse notch past the edge card, so
	 * those never collide. When two neighbors have no integer between them the lane is
	 * renumbered to a fresh sparse sequence and the shifted cards come back in Reindex.
	 * Returns { NewSort, Reindex: [{ Card, SortOrder }] }.
	 */
	_sortForSlot(pDest, pIndex)
	{
		let tmpPrev = (pIndex > 0) ? pDest[pIndex - 1] : null;
		let tmpNext = (pIndex < pDest.length) ? pDest[pIndex] : null;

		if (!tmpPrev && !tmpNext) { return { NewSort: _SORT_STEP, Reindex: [] }; }
		if (!tmpPrev) { return { NewSort: tmpNext.SortOrder - _SORT_STEP, Reindex: [] }; }
		if (!tmpNext) { return { NewSort: tmpPrev.SortOrder + _SORT_STEP, Reindex: [] }; }

		let tmpMid = Math.floor((tmpPrev.SortOrder + tmpNext.SortOrder) / 2);
		if (tmpMid > tmpPrev.SortOrder && tmpMid < tmpNext.SortOrder)
		{
			return { NewSort: tmpMid, Reindex: [] };
		}

		// No integer room between the neighbors: renumber the whole lane to a sparse
		// sequence, reserving the slot at pIndex for the dropped card.
		let tmpReindex = [];
		for (let j = 0; j < pDest.length; j++)
		{
			let tmpFinalPos = (j < pIndex) ? j : (j + 1);
			let tmpNewSort = (tmpFinalPos + 1) * _SORT_STEP;
			if (tmpNewSort !== pDest[j].SortOrder)
			{
				tmpReindex.push({ Card: pDest[j], SortOrder: tmpNewSort });
			}
		}
		return { NewSort: (pIndex + 1) * _SORT_STEP, Reindex: tmpReindex };
	}

	/**
	 * Optimistically move a card into a lane at an explicit position and return a
	 * snapshot the caller can hand to revertLocalMove() if the server write fails.
	 * pTargetIndex is the resting index among the target lane's OTHER cards (the moved
	 * card excluded); it is clamped, so Infinity appends to the end. The snapshot's
	 * NewSort places the card at that spot, and Reindex lists any same-lane neighbors
	 * whose SortOrder had to shift to open the slot.
	 */
	applyLocalMoveAt(pKind, pID, pToLaneKey, pTargetIndex)
	{
		let tmpStore = this.store();
		let tmpFound = this._findCard(pKind, pID);
		if (!tmpFound) return null;
		let tmpCard = tmpFound.Card;
		let tmpFromLane = tmpFound.Lane;
		let tmpFromIndex = tmpFound.Index;

		let tmpToLane = null;
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			if (tmpStore.Lanes[i].Key === pToLaneKey) { tmpToLane = tmpStore.Lanes[i]; break; }
		}
		if (!tmpToLane) return null;

		// Remove from the origin first so the slot math runs against the cards that
		// will actually surround the dropped card (matters for a same-lane reorder).
		tmpFromLane.Cards.splice(tmpFromIndex, 1);
		tmpFromLane.Count = tmpFromLane.Cards.length;

		let tmpDest = tmpToLane.Cards;
		let tmpIndex = Math.max(0, Math.min(pTargetIndex, tmpDest.length));
		let tmpPlan = this._sortForSlot(tmpDest, tmpIndex);

		let tmpSnapshot =
		{
			Kind: pKind,
			ID: pID,
			Card: tmpCard,
			FromLaneKey: tmpFromLane.Key,
			FromStatus: tmpCard.Status,
			FromSortOrder: tmpCard.SortOrder,
			FromIndex: tmpFromIndex,
			ToLaneKey: pToLaneKey,
			NewSort: tmpPlan.NewSort,
			Reindex: []
		};

		// Apply (and record for rollback + persistence) any neighbor renumbering.
		for (let i = 0; i < tmpPlan.Reindex.length; i++)
		{
			let tmpR = tmpPlan.Reindex[i];
			tmpSnapshot.Reindex.push(
			{
				Kind: tmpR.Card.CardKind, ID: tmpR.Card.ID, Card: tmpR.Card,
				SortOrder: tmpR.SortOrder, PrevSortOrder: tmpR.Card.SortOrder
			});
			tmpR.Card.SortOrder = tmpR.SortOrder;
		}

		tmpCard.Status = pToLaneKey;
		tmpCard.SortOrder = tmpPlan.NewSort;
		tmpDest.splice(tmpIndex, 0, tmpCard);
		tmpToLane.Count = tmpDest.length;

		return tmpSnapshot;
	}

	/**
	 * Back-compat shim: move a card to the END of a lane. Kept for any caller that
	 * does not care about position; the board uses applyLocalMoveAt directly.
	 */
	applyLocalMove(pKind, pID, pToLaneKey)
	{
		return this.applyLocalMoveAt(pKind, pID, pToLaneKey, Infinity);
	}

	/**
	 * Undo an applyLocalMoveAt() using its returned snapshot (server write failed).
	 */
	revertLocalMove(pSnapshot)
	{
		if (!pSnapshot || !pSnapshot.Card) return;
		let tmpStore = this.store();

		// Roll the moved card out of wherever it landed.
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			let tmpIndex = tmpStore.Lanes[i].Cards.indexOf(pSnapshot.Card);
			if (tmpIndex >= 0)
			{
				tmpStore.Lanes[i].Cards.splice(tmpIndex, 1);
				tmpStore.Lanes[i].Count = tmpStore.Lanes[i].Cards.length;
				break;
			}
		}

		// Undo any neighbor renumbering, then re-sort the affected lane so its array
		// order matches the restored SortOrders.
		let tmpDirtyLanes = {};
		for (let i = 0; i < pSnapshot.Reindex.length; i++)
		{
			pSnapshot.Reindex[i].Card.SortOrder = pSnapshot.Reindex[i].PrevSortOrder;
			tmpDirtyLanes[pSnapshot.ToLaneKey] = true;
		}

		pSnapshot.Card.Status = pSnapshot.FromStatus;
		pSnapshot.Card.SortOrder = pSnapshot.FromSortOrder;
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			if (tmpStore.Lanes[i].Key === pSnapshot.FromLaneKey)
			{
				let tmpIndex = Math.min(pSnapshot.FromIndex, tmpStore.Lanes[i].Cards.length);
				tmpStore.Lanes[i].Cards.splice(tmpIndex, 0, pSnapshot.Card);
				tmpStore.Lanes[i].Count = tmpStore.Lanes[i].Cards.length;
				break;
			}
		}

		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			if (tmpDirtyLanes[tmpStore.Lanes[i].Key])
			{
				tmpStore.Lanes[i].Cards.sort((pA, pB) => pA.SortOrder - pB.SortOrder);
			}
		}
	}

	/**
	 * Persist the writes a snapshot implies: the moved card, plus any neighbors that
	 * were renumbered. A card that stayed in its lane (a pure reorder) only writes
	 * SortOrder, so it never trips the server-side status-change audit.
	 */
	persistMove(pSnapshot)
	{
		if (!pSnapshot) return Promise.resolve();
		let tmpWrites = [];
		if (pSnapshot.FromStatus === pSnapshot.ToLaneKey)
		{
			tmpWrites.push(this.reorderCard(pSnapshot.Kind, pSnapshot.ID, pSnapshot.NewSort));
		}
		else
		{
			tmpWrites.push(this.moveCard(pSnapshot.Kind, pSnapshot.ID, pSnapshot.ToLaneKey, pSnapshot.NewSort));
		}
		for (let i = 0; i < pSnapshot.Reindex.length; i++)
		{
			tmpWrites.push(this.reorderCard(pSnapshot.Reindex[i].Kind, pSnapshot.Reindex[i].ID, pSnapshot.Reindex[i].SortOrder));
		}
		return Promise.all(tmpWrites);
	}

	moveCard(pKind, pID, pToStatus, pSortOrder)
	{
		let tmpEntityType = (pKind === 'Feature') ? 'Feature' : 'WorkItem';
		let tmpIDField = (pKind === 'Feature') ? 'IDFeature' : 'IDWorkItem';
		let tmpBody = { Status: pToStatus, WorkflowStatus: pToStatus, SortOrder: pSortOrder };
		tmpBody[tmpIDField] = pID;
		// EntityProvider.updateEntity -> the shared RestClient's putJSON. A 401 on
		// this non-idempotent write is safe to replay: the auth gate rejects it
		// before the handler runs, so the move never executed on the 401 attempt.
		return libFrameworkClient.updateEntity(this.fable, tmpEntityType, tmpBody);
	}

	/**
	 * Persist a within-lane reposition: SortOrder only, no Status. meadow-endpoints
	 * updates just the supplied fields, so this leaves Status/WorkflowStatus alone and
	 * the audit hook (which watches those) stays quiet on a plain reorder.
	 */
	reorderCard(pKind, pID, pSortOrder)
	{
		let tmpEntityType = (pKind === 'Feature') ? 'Feature' : 'WorkItem';
		let tmpIDField = (pKind === 'Feature') ? 'IDFeature' : 'IDWorkItem';
		let tmpBody = { SortOrder: pSortOrder };
		tmpBody[tmpIDField] = pID;
		return libFrameworkClient.updateEntity(this.fable, tmpEntityType, tmpBody);
	}

	/**
	 * Bulk operation over the board's cards, through the one server endpoint the MCP
	 * bulk tools also call (POST /1.0/Board/Bulk), so behavior can't drift. pOperation
	 * is Move | SetPriority | SetType | Archive | Unarchive; pSelector is the shared
	 * selector ({ IDs:[WorkItemNumbers] } from a multi-select, or a filter); pValue is
	 * the target lane / priority / type (unused for Archive). Without pConfirm it is a
	 * dry run (returns Matched/Count/Change and mutates nothing); with pConfirm it
	 * applies (returns Applied/Failed/Change).
	 */
	bulkOperation(pOperation, pSelector, pValue, pConfirm)
	{
		let tmpBody = { Operation: pOperation, Selector: pSelector || {}, Confirm: !!pConfirm };
		if (pValue != null && pValue !== '') { tmpBody.Value = pValue; }
		return libFrameworkClient.postJSON(this.fable, this._url('Board/Bulk'), tmpBody);
	}

	/**
	 * Record a QC review decision on a Work Item in the Review lane. pOutcome is 'Pass' | 'Fail';
	 * pOptions carries { ReasonCode, Note, Issues } for a Fail (the did-not-pass-QC punch-list).
	 * POSTs the shared Review gate (engine-gated on content.approve; the server 403s a non-approver).
	 */
	reviewWorkItem(pID, pOutcome, pOptions)
	{
		let tmpOptions = pOptions || {};
		let tmpBody = { Outcome: pOutcome };
		if (tmpOptions.ReasonCode) { tmpBody.ReasonCode = tmpOptions.ReasonCode; }
		if (tmpOptions.Note) { tmpBody.Note = tmpOptions.Note; }
		if (Array.isArray(tmpOptions.Issues) && tmpOptions.Issues.length) { tmpBody.Issues = tmpOptions.Issues; }
		return libFrameworkClient.postJSON(this.fable, this._url('WorkItem/' + pID + '/Review'), tmpBody);
	}
}

module.exports = PictProviderBoardAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
